import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient, getApiBaseUrl } from './client'
import { queryKeys } from '../lib/query'
import type { Document } from '@1130-studio/shared'
import { useAuthStore } from '../store/authStore'

interface DocumentFilters {
  projectId?: string
  clientId?: string
  type?: string
  status?: string
}

// Fetch documents
export function useDocuments(filters?: DocumentFilters) {
  return useQuery({
    queryKey: [...queryKeys.documents.all, filters],
    queryFn: () => {
      const params = new URLSearchParams()
      if (filters?.projectId) params.set('projectId', filters.projectId)
      if (filters?.clientId) params.set('clientId', filters.clientId)
      if (filters?.type) params.set('type', filters.type)
      if (filters?.status) params.set('status', filters.status)

      const queryString = params.toString()
      const url = `/documents${queryString ? `?${queryString}` : ''}`
      return apiClient<Document[]>(url)
    },
  })
}

// Fetch documents by project
export function useDocumentsByProject(projectId: string) {
  return useQuery({
    queryKey: queryKeys.documents.byProject(projectId),
    queryFn: () => apiClient<Document[]>(`/documents?projectId=${projectId}`),
    enabled: !!projectId,
  })
}

// Fetch single document
export function useDocument(id: string) {
  return useQuery({
    queryKey: queryKeys.documents.detail(id),
    queryFn: () => apiClient<Document>(`/documents/${id}`),
    enabled: !!id,
  })
}

// Upload document mutation
export function useUploadDocument() {
  const queryClient = useQueryClient()
  const accessToken = useAuthStore((state) => state.accessToken)

  return useMutation({
    mutationFn: async ({
      file,
      name,
      type,
      projectId,
      description,
    }: {
      file: File
      name?: string
      type?: string
      projectId?: string
      description?: string
    }) => {
      const formData = new FormData()
      formData.append('file', file)
      if (name) formData.append('name', name)
      if (type) formData.append('type', type)
      if (projectId) formData.append('projectId', projectId)
      if (description) formData.append('description', description)

      const response = await fetch(`${getApiBaseUrl()}/api/documents/upload`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        body: formData,
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Upload failed')
      }

      const result = await response.json()
      return result.data as Document
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.documents.all })
      if (data.projectId) {
        queryClient.invalidateQueries({ queryKey: queryKeys.documents.byProject(data.projectId) })
      }
    },
  })
}

// Update document mutation
export function useUpdateDocument() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string
      data: {
        name?: string
        type?: string
        status?: string
        description?: string | null
        projectId?: string | null
      }
    }) =>
      apiClient<Document>(`/documents/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.documents.all })
      queryClient.invalidateQueries({ queryKey: queryKeys.documents.detail(data.id) })
      if (data.projectId) {
        queryClient.invalidateQueries({ queryKey: queryKeys.documents.byProject(data.projectId) })
      }
    },
  })
}

// Delete document mutation
export function useDeleteDocument() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient(`/documents/${id}`, { method: 'DELETE' })
      return id
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.documents.all })
    },
  })
}

// Get download URL
export function getDocumentDownloadUrl(id: string): string {
  return `${getApiBaseUrl()}/api/documents/${id}/download`
}
